import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Bookmark, Check, Copy, ExternalLink, Wrench, Route as RouteIcon, RefreshCw, Loader2, SearchCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { ErrorState } from '../common/States';
import ItemImage from './ItemImage';
import SimilarItems from './SimilarItems';
import PriceHistory from './PriceHistory';
import StockHistory from './StockHistory';
import { LaneChip, DiagnosisChip, PriorityBadge, ReturnBadge, CompletenessBar } from './Tags';
import ShortlistPicker from '../shortlists/ShortlistPicker';
import { api, errorMessage } from '../../lib/api';
import { resolveImage } from '../../lib/images';
import { diagnosisMeta, fieldLabel, universeMeta } from '../../lib/taxonomy';
import { fmtDate, fmtDec, fmtEur, fmtInt, fmtPct, fmtPrice, variantLabel, fmtDec2, fmtM3 } from '../../lib/format';
import { cn } from '../../lib/utils';

const ATTRIBUTE_KEYS = [
  'brand', 'category', 'product_type', 'google_category', 'designer', 'made_in', 'color', 'color_detail', 'material', 'size', 'dimensions', 'dim_cm',
  'ip_rating', 'kelvin', 'watt', 'socket', 'dimmable', 'outdoor', 'styles', 'season', 'unit_m3',
];

const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length);

const CODE_FIELDS = [
  ['vendor_code', 'Vendor code'],
  ['ean', 'EAN'],
  ['internal_ref', 'Odoo reference'],
  ['sku', 'SKU'],
  ['odoo_product_id', 'Odoo ID'],
];

/** Product codes people quote to suppliers and clients, each copyable in one click. */
function ProductCodes({ item }) {
  const [copied, setCopied] = useState(null);
  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } catch {
      /* clipboard blocked: the value stays selectable */
    }
  };
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border px-3 py-2.5 text-[13px]">
      {CODE_FIELDS.map(([key, label]) => {
        const value = item[key];
        return (
          <div key={key} className="min-w-0">
            <dt className="text-[11px] text-muted-foreground">{label}</dt>
            <dd className="flex min-w-0 items-center gap-1">
              {value ? (
                <>
                  <span className="truncate font-mono text-[12.5px] select-all" title={String(value)}>{value}</span>
                  <button
                    type="button"
                    onClick={() => copy(key, value)}
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label={`Copy ${label}`}
                    title={`Copy ${label}`}
                  >
                    {copied === key ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function Gallery({ item }) {
  const images = useMemo(() => {
    // the variant's own photo first, then the other product photos (no duplicates)
    const key = (u) => String(u).split('?')[0];
    const seen = new Set();
    return [item.image_url, ...(item.gallery || [])].filter((u) => {
      if (!u || seen.has(key(u))) return false;
      seen.add(key(u));
      return true;
    });
  }, [item]);
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [item.odoo_product_id]);
  const current = images[idx] || null;
  return (
    <div className="space-y-2">
      <ItemImage src={current} alt={item.title} className="aspect-square w-full border p-6 sm:aspect-[4/3]" rounded="rounded-lg" iconClassName="h-14 w-14" />
      {images.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={src + i}
              type="button"
              onClick={() => setIdx(i)}
              className={cn('h-14 w-14 shrink-0 overflow-hidden rounded-md border bg-white p-1', i === idx ? 'border-foreground ring-1 ring-foreground' : 'opacity-80 hover:opacity-100')}
              aria-label={`Image ${i + 1}`}
            >
              <img src={resolveImage(src)} alt="" loading="lazy" className="h-full w-full object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveStockCheck({ odooId }) {
  const live = useMutation({ mutationFn: () => api.liveStock([odooId]) });
  const s = live.data?.stock?.[String(odooId)];
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <button
        type="button"
        onClick={() => live.mutate()}
        disabled={live.isPending}
        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-60"
      >
        {live.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        Check live stock
      </button>
      {s && (
        <span className="tabular-nums">
          Odoo now: <strong>{fmtInt(s.free)}</strong> free · {fmtInt(s.forecast)} forecast · {fmtInt(s.incoming)} incoming
        </span>
      )}
      {live.data && !s && <span className="text-muted-foreground">No live stock returned.</span>}
      {live.error && <span className="text-destructive">{errorMessage(live.error)}</span>}
    </div>
  );
}

function KV({ label, value, hint, warn, className }) {
  return (
    <div className={cn('min-w-0 rounded-lg border bg-card px-2.5 py-2', warn && 'border-amber-300 bg-amber-50/60 dark:border-amber-500/40 dark:bg-amber-500/10', className)}>
      <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="truncate text-sm font-medium tabular-nums" title={typeof value === 'string' ? value : undefined}>
        {value}
      </div>
      {hint && <div className="truncate text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, children, className }) {
  return (
    <section className={cn('space-y-2', className)}>
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        {title}
      </h3>
      {children}
    </section>
  );
}

function DetailBody({ data, onOpenItem }) {
  const { item, siblings = [], shortlists = [] } = data;
  const attrs = ATTRIBUTE_KEYS.filter((k) => !isEmpty(item[k])).map((k) => [k, Array.isArray(item[k]) ? item[k].join(', ') : String(item[k])]);
  const headroomLow = item.price_headroom != null && Number(item.price_headroom) < 1;

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Gallery item={item} />
        <div className="min-w-0 space-y-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{item.brand}</div>
            <DialogTitle className="text-lg font-semibold leading-snug">{item.title}</DialogTitle>
            <DialogDescription className="text-sm">{variantLabel(item.variant_title) || item.product_type || '\u00a0'}</DialogDescription>
          </div>
          <div className="flex flex-wrap gap-1">
            <PriorityBadge priority={item.priority} />
            <LaneChip lane={item.exit_lane} />
            <DiagnosisChip diagnosis={item.diagnosis} />
            {item.universe && <Badge variant="outline" title={universeMeta(item.universe).description}>{item.universe}</Badge>}
            {item.supplier_return_candidate && <ReturnBadge />}
            {item.bulky && <Badge variant="warning">Bulky</Badge>}
          </div>
          {item.priority_reason && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              <span className="font-medium text-foreground">{item.priority}:</span> {item.priority_reason}
            </p>
          )}
          <div className="grid grid-cols-3 gap-2">
            <KV label="Free qty" value={fmtInt(item.free_qty)} />
            <KV label="Free value" value={fmtEur(item.free_value)} />
            <KV label="Shop price" value={fmtPrice(item.price)} />
          </div>
          <LiveStockCheck key={item.odoo_product_id} odooId={item.odoo_product_id} />
          <div className="flex flex-wrap items-center gap-2">
            <ShortlistPicker ids={[item.odoo_product_id]} align="start" />
            {item.product_url && (
              <a
                href={item.product_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                Open on lamptwist.com <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {item.odoo_url && (
              <a
                href={item.odoo_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                Open in Odoo <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
          {shortlists.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <Bookmark className="h-3.5 w-3.5" /> On:
              {shortlists.map((s) => (
                <Link key={s.id} to={`/shortlists/${s.id}`} className="rounded bg-muted px-1.5 py-0.5 font-medium text-foreground hover:underline">
                  {s.name}
                </Link>
              ))}
            </div>
          )}
          <ProductCodes item={item} />
        </div>
      </div>

      {(item.diagnosis || item.fix_first || item.exit_route) && (
        <div className="grid gap-2 lg:grid-cols-3">
          {item.diagnosis && (
            <div className="rounded-lg border bg-muted/40 p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <SearchCheck className="h-3.5 w-3.5" /> Why it&apos;s stuck
              </div>
              <p className="text-sm font-semibold">{diagnosisMeta(item.diagnosis).label}</p>
              <p className="mt-0.5 text-sm leading-relaxed text-foreground/80">{item.diagnosis_reason || diagnosisMeta(item.diagnosis).description}</p>
            </div>
          )}
          {item.fix_first && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                <Wrench className="h-3.5 w-3.5" /> Fix first
              </div>
              <p className="text-sm leading-relaxed">{item.fix_first}</p>
            </div>
          )}
          {item.exit_route && (
            <div className="rounded-lg border border-teal-200 bg-teal-50/70 p-3 dark:border-teal-500/30 dark:bg-teal-500/10">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-300">
                <RouteIcon className="h-3.5 w-3.5" /> Way out
              </div>
              <p className="text-sm font-semibold">{item.exit_lane}</p>
              {item.exit_reason && <p className="mt-0.5 text-sm leading-relaxed text-foreground/80">{item.exit_reason}</p>}
              <p className="mt-1.5 text-sm leading-relaxed"><span className="font-medium">How: </span>{item.exit_route}</p>
            </div>
          )}
        </div>
      )}

      <Section title="Economics">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <KV label="Unit cost" value={fmtPrice(item.unit_cost)} hint="ex-VAT" />
          <KV label="Free value" value={fmtEur(item.free_value)} hint="at cost" />
          <KV label="Shop price" value={fmtPrice(item.price)} hint="incl. VAT" />
          <KV label="RRP" value={fmtPrice(item.rrp)} />
          <KV label="Markdown vs RRP" value={fmtPct(item.markdown_vs_rrp)} />
          <KV label="Break-even price" value={fmtPrice(item.breakeven_price)} />
          <KV
            label="Price headroom"
            value={
              <span className="inline-flex items-center gap-1">
                {headroomLow && <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                {item.price_headroom != null ? `${fmtDec2(item.price_headroom)}×` : '—'}
              </span>
            }
            hint={headroomLow ? 'Below break-even' : 'shop price / break-even'}
            warn={headroomLow}
          />
          <KV label="Volume held" value={item.m3_held != null ? `${fmtM3(item.m3_held)} m³` : '—'} hint={item.unit_m3 != null ? `${fmtM3(item.unit_m3)} m³ / unit` : undefined} />
        </div>
        {headroomLow && (
          <p className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            The current shop price is below break-even: every sale at this price loses money.
          </p>
        )}
      </Section>

      <PriceHistory odooId={item.odoo_product_id} />

      <StockHistory odooId={item.odoo_product_id} />

      <Section title="Signals">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <KV label="Age" value={item.age_days != null ? `${fmtInt(item.age_days)} days` : '—'} />
          <KV label="Sold 6m" value={fmtInt(item.sold_6m)} />
          <KV label="Web sold 12m" value={fmtInt(item.web_sold_12m)} />
          <KV label="B2B sold 12m" value={fmtInt(item.b2b_sold_12m)} />
          <KV label="Ad impressions 6m" value={fmtInt(item.impressions_6m)} />
          <KV label="Selling siblings" value={fmtInt(item.selling_siblings)} />
          <KV label="Months cover" value={item.months_cover != null ? fmtDec(item.months_cover) : '∞'} />
          <KV label="Last sale" value={fmtDate(item.last_sale_date, 'Never')} />
          <KV label="Last receipt" value={fmtDate(item.last_receipt_date)} />
          <KV label="Last vendor" value={item.last_vendor || '—'} className="col-span-2 sm:col-span-3" />
        </div>
        {item.liquidation_campaigns && (
          <p className="text-xs text-muted-foreground">
            Liquidation campaigns: <span className="font-medium text-foreground">{item.liquidation_campaigns}</span>
          </p>
        )}
      </Section>

      <div className="grid gap-6 md:grid-cols-2">
        <Section title="Listing completeness">
          <CompletenessBar value={item.completeness} className="max-w-xs" />
          {item.missing_fields?.length ? (
            <div className="flex flex-wrap gap-1">
              {item.missing_fields.map((f) => (
                <span key={f} className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] text-red-700 dark:bg-red-500/10 dark:text-red-300">
                  {fieldLabel(f)}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Nothing missing.</p>
          )}
        </Section>

        <Section title="Attributes">
          {attrs.length ? (
            <dl className="divide-y rounded-lg border text-sm">
              {attrs.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[120px_1fr] gap-2 px-2.5 py-1.5">
                  <dt className="text-muted-foreground">{fieldLabel(k)}</dt>
                  <dd className="min-w-0 break-words">{v}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-xs text-muted-foreground">No attributes filled in.</p>
          )}
        </Section>
      </div>

      {siblings.length > 0 && (
        <Section title={`Family variants (${siblings.length})`}>
          <div className="divide-y rounded-lg border text-sm">
            {siblings.map((s) => {
              const content = (
                <>
                  <div className="min-w-0">
                    <div className="truncate">{variantLabel(s.variant_title) || s.title}</div>
                    {variantLabel(s.variant_title) && <div className="truncate text-[11px] text-muted-foreground">{s.title}</div>}
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-xs tabular-nums text-muted-foreground">
                    <span>{fmtPrice(s.price)}</span>
                    <span>{fmtInt(s.free_qty)} free</span>
                    <span className={cn(s.sold_6m > 0 && 'font-medium text-emerald-700 dark:text-emerald-400')}>{fmtInt(s.sold_6m)} sold 6m</span>
                    {s.is_problem ? <Badge variant="danger">problem</Badge> : <Badge variant="muted">ok</Badge>}
                    {s.is_problem && <ArrowRight className="h-3.5 w-3.5" />}
                  </div>
                </>
              );
              return s.is_problem && s.odoo_product_id !== item.odoo_product_id ? (
                <button
                  key={s.odoo_product_id}
                  type="button"
                  onClick={() => onOpenItem?.(s.odoo_product_id)}
                  className="flex w-full items-center justify-between gap-3 px-2.5 py-2 text-left hover:bg-muted/50"
                >
                  {content}
                </button>
              ) : (
                <div key={s.odoo_product_id} className={cn('flex items-center justify-between gap-3 px-2.5 py-2', s.odoo_product_id === item.odoo_product_id && 'bg-muted/40')}>
                  {content}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <SimilarItems odooId={item.odoo_product_id} onOpen={(r) => onOpenItem?.(r.odoo_product_id)} />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="grid gap-5 md:grid-cols-2">
        <Skeleton className="aspect-[4/3] w-full rounded-lg" />
        <div className="space-y-3">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-14 w-full" />
        </div>
      </div>
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

/** Right-side item detail. `odooId` null = closed. */
export default function ItemDetailSheet({ odooId, onClose, onOpenItem }) {
  const open = !!odooId;
  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 });
  }, [odooId]);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['item', String(odooId)],
    queryFn: () => api.item(odooId),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        ref={scrollRef}
        className="block max-h-[90vh] w-[calc(100%-1.5rem)] max-w-[1100px] overflow-y-auto p-0 sm:w-[calc(100%-4rem)]"
        aria-describedby={undefined}
      >
        {isLoading && (
          <>
            <DialogTitle className="sr-only">Loading item</DialogTitle>
            <DetailSkeleton />
          </>
        )}
        {error && (
          <div className="p-6">
            <DialogTitle className="mb-3">Item #{odooId}</DialogTitle>
            <ErrorState error={error} onRetry={refetch} title="Could not load this item" />
          </div>
        )}
        {data?.item && <DetailBody data={data} onOpenItem={onOpenItem} />}
      </DialogContent>
    </Dialog>
  );
}
